import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

import TranslationNS from '@/utils/Constants/TranslationNS'
import { useAuth } from '@/middlewares/auth/auth-context'
import { consetPreferenceGiven, readAnalyticsConsent, saveConsent } from './index.utils'

import './index.css'

const isPreviewPage = (path) => {
    const parts = path.split('/').filter((x) => x)
    if (['leaderboard', 'planning'].includes(parts[1]) && ['tv', 'stream'].includes(parts[2])) return true
    if (parts[1] === 'live' && parts[2] === 'screen') return true // the Live Judging big screen (kiosk)
    return false
}

function CookieIcon() {
    return (
        <svg
            className="cookies-consent-ico"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
        >
            <path d="M21 12.5a9 9 0 1 1-9.5-9.5 3.5 3.5 0 0 0 4 4 3.5 3.5 0 0 0 5.5 5.5z" />
            <circle cx="8.5" cy="10" r=".9" fill="currentColor" stroke="none" />
            <circle cx="9.5" cy="15.5" r=".9" fill="currentColor" stroke="none" />
            <circle cx="14.5" cy="14.5" r=".9" fill="currentColor" stroke="none" />
            <circle cx="12.5" cy="10.5" r=".9" fill="currentColor" stroke="none" />
        </svg>
    )
}

/**
 * Cookie card (mock `_kit/cookies.html`, Sept 26–30 2026): one floating card, two views.
 * Banner: "Continue without accepting" top right (reject all in one click, CNIL), Accept all ·
 * Choose. Preferences: ‹ Back, the categories (Essential always on, Audience measurement switch,
 * off until the visitor says otherwise), Save my choices · Accept all.
 * `authContext.cookiesConsentOpen` is `false | true | 'prefs'` — My Account opens the panel directly.
 */
export default function CookiesConsent() {
    const { t } = useTranslation(TranslationNS.COOKIES_CONSENT)

    const authContext = useAuth()
    const location = useLocation()
    const open = authContext.cookiesConsentOpen

    const [view, setView] = useState('banner')
    const [analytics, setAnalytics] = useState(false)
    const firstActionRef = useRef(null)
    const focusOnView = useRef(false)

    useEffect(() => {
        if (!consetPreferenceGiven()) authContext.setCookiesConsentOpen(true)
    }, [])

    useEffect(() => {
        document.body.classList.toggle('cookies-open', !!open)
        return () => document.body.classList.remove('cookies-open')
    }, [open])

    // Each opening picks its view and pre-fills the switch from the stored choice.
    useEffect(() => {
        if (!open) return
        setView(open === 'prefs' ? 'prefs' : 'banner')
        setAnalytics(readAnalyticsConsent())
    }, [open])

    // Switching views by click moves focus to the first action (mock `view(v, focus)`); opening does not.
    useEffect(() => {
        if (!focusOnView.current) return
        focusOnView.current = false
        firstActionRef.current?.focus()
    }, [view])

    const switchView = (next) => {
        focusOnView.current = true
        setView(next)
    }

    const decide = (analyticsAccepted) => {
        authContext.setCookiesConsentOpen(false)
        saveConsent(analyticsAccepted)
    }

    if (isPreviewPage(location.pathname) || !open) return null

    const skip = (
        <button type="button" className="cookies-consent-skip" onClick={() => decide(false)}>
            {t('Continue without accepting')}
        </button>
    )

    return (
        <div className="cookies-consent" role="region" aria-label={t('We use cookies')}>
            {view === 'banner' ? (
                <>
                    <div className="cookies-consent-head">
                        <div className="cookies-consent-head-l">
                            <CookieIcon />
                            <p className="cookies-consent-ttl">{t('We use cookies')}</p>
                        </div>
                        {skip}
                    </div>
                    <p className="cookies-consent-txt">
                        {t(
                            'Essential cookies keep the site running. With your consent we also measure traffic — it tells us which leaderboards load slowly on competition day, so we can fix them.',
                        )}
                    </p>
                    <p className="cookies-consent-links">
                        <a href="https://info.scoring.fit/termsofuse" target="_blank" rel="noreferrer">
                            {t('Terms of use')}
                        </a>
                        <a href="https://info.scoring.fit/privacypolicy" target="_blank" rel="noreferrer">
                            {t('Privacy Information')}
                        </a>
                    </p>
                    <div className="cookies-consent-actions">
                        <button
                            ref={firstActionRef}
                            type="button"
                            className="cookies-consent-btn cookies-consent-btn-primary"
                            onClick={() => decide(true)}
                        >
                            {t('Accept all')}
                        </button>
                        <button
                            type="button"
                            className="cookies-consent-btn cookies-consent-btn-ghost"
                            onClick={() => switchView('prefs')}
                        >
                            {t('Choose')}
                        </button>
                    </div>
                </>
            ) : (
                <>
                    <div className="cookies-consent-head">
                        <button type="button" className="cookies-consent-back" onClick={() => switchView('banner')}>
                            <svg
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="1.8"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                aria-hidden
                            >
                                <path d="M15 18l-6-6 6-6" />
                            </svg>
                            <span>{t('Back')}</span>
                        </button>
                        {skip}
                    </div>
                    <p className="cookies-consent-ttl">{t('Cookies preferences')}</p>
                    <ul className="cookies-consent-cats">
                        <li className="cookies-consent-cat">
                            <div className="cookies-consent-cat-txt">
                                <p className="cookies-consent-cat-ttl">{t('Essential')}</p>
                                <p className="cookies-consent-cat-sub">
                                    {t(
                                        'Sign-in, language, your registration in progress, secure payment (Stripe), support chat (Crisp). The site cannot work without them.',
                                    )}
                                </p>
                            </div>
                            <span className="cookies-consent-always">{t('Always on')}</span>
                        </li>
                        <li className="cookies-consent-cat">
                            <div className="cookies-consent-cat-txt">
                                <p className="cookies-consent-cat-ttl" id="cookies-consent-analytics">
                                    {t('Audience measurement')}
                                </p>
                                <p className="cookies-consent-cat-sub">
                                    {t('Anonymous visit statistics: which pages are used, which ones are slow.')}
                                </p>
                            </div>
                            <label className="cookies-consent-tgl-row">
                                <input
                                    type="checkbox"
                                    role="switch"
                                    className="cookies-consent-tgl"
                                    checked={analytics}
                                    onChange={(event) => setAnalytics(event.target.checked)}
                                    aria-labelledby="cookies-consent-analytics"
                                />
                                <span className="cookies-consent-tgl-k" aria-hidden />
                            </label>
                        </li>
                    </ul>
                    <div className="cookies-consent-actions">
                        <button
                            ref={firstActionRef}
                            type="button"
                            className="cookies-consent-btn cookies-consent-btn-primary"
                            onClick={() => decide(analytics)}
                        >
                            {t('Save my choices')}
                        </button>
                        <button
                            type="button"
                            className="cookies-consent-btn cookies-consent-btn-ghost"
                            onClick={() => decide(true)}
                        >
                            {t('Accept all')}
                        </button>
                    </div>
                </>
            )}
        </div>
    )
}
